import { AppDispatch } from 'app/store';
import { delay } from 'utils';
import { API_ENDPOINTS } from 'utils/constants';
import { deleteAPI, getAPI, postAPI, putAPI } from 'services/api/apiAsync';
import { fetchUserDetails } from 'services/user/userAsync';
import {
  setPlanEntitlements,
  setAvailableOffers,
  setPlanChangeEligibility,
} from './planSlice';
import { ICancelRequest } from './types';

export function getUserPlan() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.planDetails}`,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function findPlanDetails(planCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.findPlan,
        data: {
          plan_code: planCode,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      if (response[0]?.entitlements) {
        dispatch(setPlanEntitlements(response[0].entitlements));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchAvailableOffers() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.getAvailableOffers,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(setAvailableOffers(response ?? []));
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchPlanChangeEligibility() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.eligibility,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      if (response) {
        dispatch(setPlanChangeEligibility(response));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function submitPlanChange(offerCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.changeMarketingOffer}/${offerCode}`,
        data: {},
      };
      const response = await dispatch(putAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* The DELETE resolves before the cancellation is persisted, so an immediate refetch
   can still read an active account. */
const POST_CANCEL_REFRESH_DELAY_MS = 2000;

export function cancelPlan(userId: string, requestReason: ICancelRequest) {
  /* No try/catch here: there is no value to return and nothing to add to the
     error, so letting the rejection propagate is identical for the caller — the
     orchestrator already wraps this dispatch to raise the error toast — and it
     avoids a placeholder return just to satisfy `consistent-return`. */
  return async (dispatch: AppDispatch) => {
    const config = {
      url: `${API_ENDPOINTS.userProfile}/${userId}`,
      data: requestReason,
    };

    /**
     * Success is *any* resolved response, matching identityWeb
     * (`account-cancel-modal.component.ts:82-95`), which is the functional
     * authority. Angular puts its whole success path in the RxJS `next` handler and
     * raises the banner only from `error`, so every 2xx confirms the cancellation.
     * axios draws the same line — it rejects 4xx and 5xx — so "resolved" here means
     * "2xx", exactly as in Angular.
     *
     * This replaces a React-only status allowlist. BRD FR-038 / AC-022 / decision
     * D10 rejected anything but `202 Accepted`, on the theory that another 2xx meant
     * the cancellation had not really happened. Browser verification on 2026-08-21
     * falsified it: the live endpoint answers **`204 No Content`**, so a genuinely
     * accepted cancellation was rejected — the member saw "Membership cannot be
     * cancelled. Please try again.", no refetch fired, and Billing details kept
     * showing an active membership while the account moved to
     * `pending cancellation` server-side. A retry then "failed" again against an
     * already-cancelled account. Every earlier pass stubbed a 202, which is why the
     * gate read as correct until a real cancellation was performed.
     *
     * Requester direction (2026-08-21) is to match Angular rather than keep a status
     * whitelist. That also makes this immune to the same defect should the endpoint
     * ever answer 200 or 201.
     *
     * ⚠ BRD FR-038 / AC-022 need an amendment to record this.
     */
    await dispatch(deleteAPI(config)).unwrap();

    await delay(POST_CANCEL_REFRESH_DELAY_MS);

    /**
     * Refreshed unconditionally on success — a deliberate improvement on Angular,
     * which gates only this refresh on `res.status === 202` and so leaves
     * `user.status` stale on the real 204 until something else refetches.
     * Eligibility is refreshed alongside the user record because it drives the entry
     * link's disabled state (BRD FR-002 / FR-032 / BR-024); Angular refreshes it
     * here too, unconditionally (`:89`).
     *
     * `allSettled` is deliberate: a refresh failure must never turn a successful
     * cancellation into a failed one. The member *is* cancelled, so a stale card is
     * the lesser evil against an error banner.
     */
    await Promise.allSettled([
      dispatch(fetchUserDetails(userId)),
      dispatch(fetchPlanChangeEligibility()),
    ]);
  };
}
